import styles from '../css/ServiceManager.module.css';
import type { LiveStateView, StatusVariant } from '../types/ServiceManager.types';

/**
 * Bootstrap text utility per status variant, resolved from the theme tokens.
 *
 * Exported because the Deployments tab colours its per-row toggle outcomes
 * from the same scale. Two copies would drift the first time a variant was
 * retuned.
 */
export const VARIANT_TEXT_CLASS: Record<StatusVariant, string> = {
  success: 'text-success',
  danger: 'text-danger',
  warning: 'text-warning',
  primary: 'text-info',
  secondary: 'text-theme-muted',
};

interface StatusDotProps {
  /** Label, hover hint and colour variant for the state being shown. */
  view: LiveStateView;
  /**
   * A short line rendered under the label when the row needs qualifying, such
   * as "Enabled, but not running". Carries its own colour.
   */
  note?: string;
  noteVariant?: StatusVariant;
}

/**
 * A service's state as a coloured dot plus its label.
 *
 * Shared by both tabs so "Running is green" has one definition. The Services
 * tab shows a mapped PM2 status and the Deployments tab shows the live PM2
 * state, but they are the same fact to an operator scanning the page, and two
 * copies of the colour mapping would drift the moment one of them was themed.
 *
 * Colour is never the only cue: the label always spells the state out, which is
 * what keeps this readable in the two high-contrast modes and for anyone who
 * cannot separate the dot colours.
 */
export default function StatusDot({ view, note, noteVariant = 'warning' }: StatusDotProps) {
  return (
    <div className="d-flex flex-column gap-1">
      <span
        className={`fw-semibold small ${styles.statusChip} ${VARIANT_TEXT_CLASS[view.variant]}`}
        title={view.hint}
      >
        <span
          aria-hidden="true"
          className={`${styles.statusDot} ${styles[`statusDot${view.variant}`]}`}
        />
        {view.label}
      </span>
      {note && <span className={`fw-bold small ${VARIANT_TEXT_CLASS[noteVariant]}`}>{note}</span>}
    </div>
  );
}
